import React from "react";
import { formatDecimal, isDiscountActive, todayIso } from "./helpers";

/**
 * Lists granted discounts with their state today.
 *
 * @param {object} props - Component props.
 * @param {object[]} props.discounts - Discount rows from the API.
 * @param {Function} props.onEdit - Called with the row to amend.
 * @param {Function} props.onDelete - Called with the row to withdraw.
 * @param {number|null} props.busyId - Row currently being withdrawn.
 * @returns {JSX.Element} The discount table.
 *
 * @remarks
 * Shows whether each row is in force today rather than only its dates. A window
 * that has quietly expired looks identical to a live one in a date column, and
 * that is exactly the mistake worth catching on a pricing screen.
 */
const DiscountTable = ({ discounts, onEdit, onDelete, busyId }) => {
  const today = todayIso();

  if (!discounts.length) {
    return (
      <div className="aicr-card">
        <h2>Company discounts</h2>
        <p className="aicr-muted">
          No discounts granted. Every company pays face value for credits.
        </p>
      </div>
    );
  }

  return (
    <div className="aicr-card">
      <h2>Company discounts</h2>
      <p className="aicr-card-sub">
        A company without a row here pays face value.
      </p>

      <div className="aicr-table-wrap">
        <table className="aicr-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Discount</th>
              <th>Valid from</th>
              <th>Valid to</th>
              <th>State</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {discounts.map((discount) => {
              const active = isDiscountActive(discount, today);

              return (
                <tr key={discount.id}>
                  <td>
                    {discount.companyName || `Company ${discount.companyId}`}
                  </td>
                  <td className="aicr-num">
                    {formatDecimal(discount.discountPercent, 3)}%
                  </td>
                  <td className="aicr-num">
                    {String(discount.validFrom).slice(0, 10)}
                  </td>
                  <td className="aicr-num">
                    {discount.validTo
                      ? String(discount.validTo).slice(0, 10)
                      : "Open-ended"}
                  </td>
                  <td>
                    <span
                      className={`aicr-pill ${
                        active ? "aicr-pill-active" : "aicr-pill-inactive"
                      }`}
                    >
                      {active ? "In force" : "Not in force"}
                    </span>
                  </td>
                  <td>
                    <div className="aicr-row-actions">
                      <button
                        type="button"
                        className="aicr-btn aicr-btn-secondary"
                        onClick={() => onEdit(discount)}
                      >
                        Amend
                      </button>
                      <button
                        type="button"
                        className="aicr-btn aicr-btn-danger"
                        onClick={() => onDelete(discount)}
                        disabled={busyId === discount.id}
                      >
                        {busyId === discount.id ? "Removing…" : "Withdraw"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DiscountTable;
